import { ICONS } from '@qlevr/shared/constants';
import { ImageInterface, ProductInterface } from '@qlevr/shared/interfaces';
import { ProductBundlerContext } from '@qlevr/shared/providers';
import { getVendorProductUrl, reverseEscapeUrl } from '@qlevr/shared/utilities';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useContext, useEffect, useMemo, useState } from 'react';
import IconRenderer from '../../../icons/icon-renderer.component';
import ImageRenderer from '../../../image-renderer/image-renderer';

export interface ProductBundlerCardProps {
  product: ProductInterface;
}

export function ProductBundlerMainProductCard(props: ProductBundlerCardProps) {
  const { variant, addVariantId, removeVariantId } = useContext(ProductBundlerContext);
  const [activeImage, setActiveImage] = useState<ImageInterface | null>(props.product.featuredImage);
  const { locale } = useParams<{ locale: string }>();

  const t = useTranslations();
  const vendorProductUrl = useMemo(
    () => getVendorProductUrl({ productSlug: props.product.slug, vendor: props.product.vendor, locale }),
    [props.product.slug, props.product.vendor, locale],
  );

  useEffect(() => {
    if (variant) {
      addVariantId(variant.id); // Add the selected variant ID
    } else {
      setActiveImage(props.product.featuredImage);
    }

    // Clean up previous variant ID
    return () => {
      if (variant) {
        removeVariantId(variant.id); // Remove the previous variant ID
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  return (
    <div className="relative flex flex-col items-end justify-between space-y-2 rounded-lg border-gray-300 bg-gray-100 p-2">
      <div className="flex w-full">
        {activeImage && (
          <Link href={vendorProductUrl} target="_blank">
            <ImageRenderer image={activeImage} className="mr-4 h-20 w-full max-w-20 rounded-lg" />
          </Link>
        )}
        <div className="w-full">
          <div className="flex flex-wrap justify-between gap-1">
            <div className="text-base font-bold">{props.product.title}</div>

            <Link
              href={vendorProductUrl}
              target="_blank"
              className="flex cursor-pointer items-center gap-1 text-sm font-medium lowercase text-black underline"
            >
              {t('productBundler.viewProduct')}
              <IconRenderer icon={ICONS.alertCircle} className="h-4 w-4" />
            </Link>
          </div>
          <div className="mb-1 flex flex-col text-xs font-normal leading-5">
            {variant?.options.map((option) => (
              <span key={option.name}>
                <span className="capitalize">{option.name}:</span> {reverseEscapeUrl(option.value)}
              </span>
            ))}
          </div>
          <div className="flex gap-2 font-serif text-base">
            <span className="">
              {variant?.price?.amount && (
                <span>
                  {variant.price.symbol}
                  {variant.price.amount}
                </span>
              )}
            </span>
            {/* {props.product.discountPercentage && variant?.price?.amount && (
              <span>
                {props.product?.priceRange?.minVariantPrice.symbol}
                {calculateDiscountPrice(variant?.price?.amount, props.product.discountPercentage ?? 0)}
              </span>
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductBundlerMainProductCard;
